import { useState, useEffect } from 'react';
import { useAuth } from '../../shared/context/AuthContext';
import supabase from '../../../config/supabaseClient';

/**
 * Hook para verificar permissões de gestão de usuários
 * Verifica se o usuário atual pode criar, editar ou gerenciar outros usuários
 */
export const useUserManagementPermissions = () => {
  const { user, session } = useAuth();
  const [permissions, setPermissions] = useState({
    canCreateUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canViewUsers: false,
    isAdmin: false,
    userLevel: null,
    loading: true
  });

  useEffect(() => {
    const checkPermissions = async () => {
      // Aguardar um pouco para garantir que o user está totalmente carregado
      if (!user || !session) {
        if (!user && !session) {
          // Ainda carregando
          setPermissions(prev => ({ ...prev, loading: true }));
          return;
        }
        
        // Não autenticado
        setPermissions({
          canCreateUsers: false,
          canEditUsers: false,
          canDeleteUsers: false,
          canViewUsers: false,
          isAdmin: false,
          userLevel: null,
          loading: false
        });
        return;
      }

      try {
        console.log('Verificando permissões para usuário:', user.id);
        
        // Buscar dados do usuário na tabela usuarios
        const { data: userData, error } = await supabase
          .from('funcionariousuario')
          .select('id, nivel_acesso, permissoes, ativo')
          .eq('uid', user.id)
          .single();

        console.log('Dados do usuário encontrados:', userData);

        if (error) {
          console.error('Erro ao verificar permissões:', error);
          setPermissions({
            canCreateUsers: false,
            canEditUsers: false,
            canDeleteUsers: false,
            canViewUsers: false,
            isAdmin: false,
            userLevel: null,
            loading: false,
            error: error.message
          });
          return;
        }

        if (!userData) {
          console.warn('Usuário não encontrado na tabela usuarios');
          setPermissions({
            canCreateUsers: false,
            canEditUsers: false,
            canDeleteUsers: false,
            canViewUsers: false,
            isAdmin: false,
            userLevel: null,
            loading: false,
            error: 'Usuário não encontrado na base de dados'
          });
          return;
        }

        if (!userData.ativo) {
          console.warn('Usuário inativo');
          setPermissions({
            canCreateUsers: false,
            canEditUsers: false,
            canDeleteUsers: false,
            canViewUsers: false,
            isAdmin: false,
            userLevel: userData.nivel_acesso,
            loading: false,
            error: 'Usuário inativo'
          });
          return;
        }

        const isAdmin = userData.nivel_acesso === 'admin';
        const hasUserManagementPermission = userData.permissoes?.['gestao-usuarios'] === true;

        console.log('Verificação de permissões:', {
          isAdmin,
          hasUserManagementPermission,
          nivel_acesso: userData.nivel_acesso,
          permissoes: userData.permissoes
        });

        // Definir permissões baseadas no nível e permissões específicas
        const newPermissions = {
          canCreateUsers: isAdmin && hasUserManagementPermission,
          canEditUsers: isAdmin && hasUserManagementPermission,
          canDeleteUsers: isAdmin && hasUserManagementPermission,
          canViewUsers: isAdmin && hasUserManagementPermission,
          isAdmin: isAdmin,
          userLevel: userData.nivel_acesso,
          loading: false,
          userData: userData,
          error: null
        };

        console.log('Permissões calculadas:', newPermissions);
        setPermissions(newPermissions);

      } catch (error) {
        console.error('Erro ao verificar permissões:', error);
        setPermissions({
          canCreateUsers: false,
          canEditUsers: false,
          canDeleteUsers: false,
          canViewUsers: false,
          isAdmin: false,
          userLevel: null,
          loading: false,
          error: error.message
        });
      }
    };

    // Aguardar um pouco antes de verificar permissões
    const timeoutId = setTimeout(checkPermissions, 100);
    
    return () => clearTimeout(timeoutId);
  }, [user, session]);

  // Função para verificar permissão específica
  const hasPermission = (permission) => {
    return permissions[permission] || false;
  };

  // Função para verificar se pode editar usuário específico
  const canEditUser = (targetUserId, targetUserLevel) => {
    if (!permissions.canEditUsers) return false;
    
    // Admin pode editar qualquer usuário, exceto outros admins (a menos que seja o primeiro admin)
    if (permissions.isAdmin) {
      // Se o usuário alvo é admin, só pode editar se for o primeiro admin (id = 1)
      if (targetUserLevel === 'admin') {
        return permissions.userData?.id === 1; // Apenas o primeiro admin pode editar outros admins
      }
      return true;
    }
    
    return false;
  };

  // Função para verificar se pode criar usuário com nível específico
  const canCreateUserWithLevel = (targetLevel) => {
    if (!permissions.canCreateUsers) return false;
    
    // Admin pode criar qualquer nível, exceto outros admins (a menos que seja o primeiro admin)
    if (permissions.isAdmin) {
      if (targetLevel === 'admin') {
        return permissions.userData?.id === 1; // Apenas o primeiro admin pode criar outros admins
      }
      return true;
    }
    
    return false;
  };

  return {
    ...permissions,
    hasPermission,
    canEditUser,
    canCreateUserWithLevel,
    // Funções de conveniência
    canAccessUserManagement: permissions.canViewUsers,
    canManageUsers: permissions.canCreateUsers || permissions.canEditUsers
  };
};

export default useUserManagementPermissions;